import { useEffect, useState } from 'react';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { EmptyMessage } from '../../components/EmptyMessage';
import { InlineFeedback } from '../../components/InlineFeedback';
import { FilterMenu } from '../../components/FilterMenu';
import { ItemCard } from '../../components/ItemCard';
import { Section, Title } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { DetailModal } from '../../composites/DetailModal';
import { TitleRow } from './AccommodationListTemplate.styles';
import type {
  AccommodationListItem,
  AccommodationListTemplateProps,
} from './AccommodationListTemplate.types';

const META_SEPARATOR = ' · ';

function buildMeta(item: AccommodationListItem): string | undefined {
  const labels = item.occupancyLabels?.filter(Boolean) ?? [];
  return labels.length > 0 ? labels.join(META_SEPARATOR) : undefined;
}

function matchesInclusions(item: AccommodationListItem, selected: string[]): boolean {
  const inclusions = item.inclusions ?? [];
  return selected.every((value) => inclusions.includes(value));
}

export function AccommodationListTemplate({
  items,
  onSelectItem,
  isLoading,
  titleLabel,
  fromPriceLabel,
  soldOutLabel,
  noResultsLabel,
  noResultsFeedback,
  noImageLabel,
  editSearchLabel,
  onEditSearch,
  columns = 2,
  imageAspectRatio,
  imageMobileAspectRatio,
  quickViewLabels,
  inclusionOptions,
  inclusionFilterLabel = 'Filter',
  noFilterResultsLabel,
}: AccommodationListTemplateProps) {
  const [quickViewId, setQuickViewId] = useState<string | null>(null);
  const [selectedInclusions, setSelectedInclusions] = useState<string[]>([]);

  const optionValues = inclusionOptions?.map((option) => option.value) ?? [];
  const activeInclusions = selectedInclusions.filter((value) => optionValues.includes(value));

  useEffect(() => {
    if (activeInclusions.length !== selectedInclusions.length) {
      setSelectedInclusions(activeInclusions);
    }
  }, [activeInclusions, selectedInclusions]);

  const filter = inclusionOptions && inclusionOptions.length > 0 && (
    <FilterMenu
      options={inclusionOptions}
      selectedValues={activeInclusions}
      onChange={setSelectedInclusions}
      label={inclusionFilterLabel}
    />
  );

  const filteredItems =
    activeInclusions.length === 0
      ? items
      : items.filter((item) => matchesInclusions(item, activeInclusions));

  const quickViewItem = items.find((item) => item.id === quickViewId) ?? null;
  const quickView = quickViewItem?.quickView;
  const quickViewImages = quickView?.images?.some((image) => image.src)
    ? quickView.images
    : quickViewItem?.imageUrl
    ? [{ src: quickViewItem.imageUrl, alt: quickViewItem.name }]
    : [];

  const {
    quickView: quickViewLabel = 'Quick view',
    inclusions: inclusionsLabel = "What's included",
    select: selectLabel = 'Book',
    close: closeLabel,
    previousImage: previousImageLabel,
    nextImage: nextImageLabel,
    goToImage: goToImageLabel,
    fromPrice: fromPriceQualifier,
  } = quickViewLabels ?? {};

  const closeQuickView = () => setQuickViewId(null);

  if (isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filter}
        </TitleRow>
        <CardGrid $columns={columns}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard
              key={index}
              variant="grid"
              aspectRatio={imageAspectRatio}
              mobileAspectRatio={imageMobileAspectRatio}
            />
          ))}
        </CardGrid>
      </Section>
    );
  }

  if (!isLoading && filteredItems.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filter}
        </TitleRow>
        {noResultsFeedback ? (
          <InlineFeedback {...noResultsFeedback} />
        ) : (
          <EmptyMessage>
            {activeInclusions.length > 0 ? noFilterResultsLabel ?? noResultsLabel : noResultsLabel}
          </EmptyMessage>
        )}
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
        {filter}
      </TitleRow>
      <ContentArea>
        <CardGrid $columns={columns}>
          {filteredItems.map((item) => (
            <ItemCard
              key={item.id}
              image={{ src: item.imageUrl, alt: item.name, placeholderLabel: noImageLabel }}
              imageAspectRatio={imageAspectRatio}
              imageMobileAspectRatio={imageMobileAspectRatio}
              title={item.name}
              meta={buildMeta(item)}
              badges={!item.unavailable ? item.badges : undefined}
              imageBadge={item.promotionName || undefined}
              imageAction={
                item.quickView
                  ? { label: quickViewLabel, onClick: () => setQuickViewId(item.id) }
                  : undefined
              }
              unavailableLabel={
                item.unavailable ? item.unavailableLabel ?? soldOutLabel : undefined
              }
              editSearchLabel={editSearchLabel}
              onEditSearch={onEditSearch}
              price={
                !item.unavailable && item.price
                  ? {
                      amount: item.price.amount,
                      currencyCode: item.price.currencyCode,
                      label: fromPriceLabel,
                      discount: item.discount,
                    }
                  : undefined
              }
              unavailable={item.unavailable}
              ariaLabel={item.name}
              onSelect={() => onSelectItem(item.id)}
            />
          ))}
        </CardGrid>
      </ContentArea>

      {quickViewItem && quickView && (
        <DetailModal
          open
          onClose={closeQuickView}
          title={quickViewItem.name}
          titleVariant="prominent"
          images={quickViewImages}
          tags={quickView.categories}
          description={quickView.description}
          pricePerStay={quickView.pricePerStay}
          amenities={quickView.inclusions}
          amenitiesHeading={inclusionsLabel}
          amenitiesVariant="badges"
          onReserve={
            quickViewItem.unavailable
              ? undefined
              : () => {
                  onSelectItem(quickViewItem.id);
                  closeQuickView();
                }
          }
          labels={{
            reserve: selectLabel,
            close: closeLabel,
            previousImage: previousImageLabel,
            nextImage: nextImageLabel,
            goToImage: goToImageLabel,
            fromPrice: fromPriceQualifier,
          }}
        />
      )}
    </Section>
  );
}
